import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';

import { bdsGridModeType } from '@bds/components';

import { CommunicationMessage, CommunicationService } from '@bds/core';
import { FastracDxGridWrapperComponent, FastracScreenBaseComponent } from '@bds/fastrac';
import { CarIdTransformService } from '@bds/helpers';

import { RtTripErrorService } from '@bds/railtrac';

import { RtTrip, RtTripRefFieldDef } from '@bds/railtrac-models';

import { FastracColsService } from '@bds/services';

import { faBars } from '@fortawesome/pro-solid-svg-icons';
import DataSource from 'devextreme/data/data_source';
import ODataStore from 'devextreme/data/odata/store';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { PageService } from '@bds/core';
import { CreateTripService } from '@bds/railtrac';
import { BdsDialogMultiConfirmComponent, BdsDialogMultiConfirmModel } from '@bds/smart-components';
import { ErrorListItem } from '@bds/rt-advance-models';
import { ErrorHandlerService } from '@bds/railtrac-internal-services';
import { Clipboard } from '@angular/cdk/clipboard';
import { InterfaceValidationSelectionService } from './interface-validation-selection.service';

interface SelectionToolbarValueChangedEvent {
    event?: Event;
}

interface SelectionToolbarItem {
    location: 'before';
    widget: 'dxCheckBox';
    options: {
        text: string;
        value: boolean | undefined;
        enableThreeStateBehavior: boolean;
        onValueChanged: (event: SelectionToolbarValueChangedEvent) => void;
    };
}

@Component({
    selector: 'app-interface-validation-screen',
    templateUrl: './interface-validation-screen.component.html',
    styleUrl: './interface-validation-screen.component.scss',
    encapsulation: ViewEncapsulation.None,
})
export class InterfaceValidationScreenComponent
    extends FastracScreenBaseComponent
    implements OnInit, OnDestroy
{
    @ViewChild('dxGridWrapper') fastracGridComponent: FastracDxGridWrapperComponent;

    public gridSource: DataSource;
    public allowModeToggle = true;
    public selectedRowKeys: number[] = [];
    public selectedRowData: any[] = [];
    public selectionToolbarItems: SelectionToolbarItem[] = [this.createSelectionToolbarItem()];
    public gridId = 134;
    public gridMode: bdsGridModeType = 'multiple';
    public gridStore: ODataStore;

    public isCreatingNew = false;
    public newTrip: RtTrip;

    public iconMenu = faBars;

    public tripRefFieldDefs$: Observable<RtTripRefFieldDef[]>;

    isDirty = false;
    errors: ErrorListItem[] = [];

    private ngUnsubscribe$: Subject<boolean> = new Subject<boolean>();

    get grid() {
        return this.fastracGridComponent?.grid;
    }

    get areAllAvailableRowsSelected(): boolean {
        const availableRowKeys = this.getAvailableRowKeys();
        const state = this.selectionService.selectionState;
        return (
            availableRowKeys.length > 0 &&
            availableRowKeys.every((ormId) => this.selectionService.isSelected(ormId.toString())) &&
            (!state.selectAll || state.excludedIds.length === 0)
        );
    }

    get isAvailableRowSelectionIndeterminate(): boolean {
        const availableRowKeys = this.getAvailableRowKeys();
        const selectedAvailableRowCount = availableRowKeys.filter((ormId) =>
            this.selectionService.isSelected(ormId.toString()),
        ).length;

        return (
            (this.selectionService.selectionState.selectAll &&
                this.selectionService.selectionState.excludedIds.length > 0) ||
            (selectedAvailableRowCount > 0 && selectedAvailableRowCount < availableRowKeys.length)
        );
    }

    constructor(
        public tripErrorService: RtTripErrorService,
        public dialog: MatDialog,
        public router: Router,
        public route: ActivatedRoute,
        pageService: PageService,
        public snackbar: MatSnackBar,
        public gridColumnService: FastracColsService,
        public carIdTransformService: CarIdTransformService,
        public communicationService: CommunicationService,
        private _createTripService: CreateTripService,
        private errorHandler: ErrorHandlerService,
        private clipboard: Clipboard,
        public selectionService: InterfaceValidationSelectionService,
    ) {
        super(router, route, gridColumnService, communicationService, carIdTransformService);

        pageService.setHeader('Interface Validation');

        communicationService
            .getData()
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe((m) => {
                const message: CommunicationMessage = m as CommunicationMessage;
                if (message && message.subscriberName === 'TripErrorScreen' && message.data) {
                    if (this.grid && this.grid.instance) {
                        void this.grid.instance.refresh();
                    }
                }
            });

        this.gridSource = this.tripErrorService.getTripErrorGridDataSource();

        this._createTripService.createTripChangeEmitted$.subscribe((x) => {
            this.createRecord();
        });

        this._createTripService.refreshTripGridChangeEmitted$.subscribe((x) => {
            this.refreshGrid();
        });
    }

    ngOnInit() {
        super.ngOnInit();
        this.grid?.instance.repaint();
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.ngUnsubscribe$.next(true);
        this.ngUnsubscribe$.unsubscribe();
    }

    onDirty(): void {
        this.isDirty = true;
    }

    refreshGrid(): void {
        void this.grid.instance.refresh();
    }

    createRecord(): void {
        void this.router.navigate(['trips/create'], { queryParamsHandling: 'preserve' });
    }

    deleteErrors(): void {
        this.selectedRowData = this.grid.instance.getSelectedRowsData();
        if (this.selectedRowData == null || this.selectedRowData.length == 0) {
            return;
        }

        let confirmInfo: BdsDialogMultiConfirmModel = {
            title: 'Delete Confirmation',
            content: `Are you sure you want to delete ${this.selectedRowKeys.length} validation(s)?`,
            actionText: 'Delete',
        };

        const dialogRef = this.dialog.open(BdsDialogMultiConfirmComponent, {
            data: confirmInfo,
        });

        dialogRef.beforeClosed().subscribe((obs) => {
            if (obs) {
                this.tripErrorService
                    .deleteErrors(this.selectedRowData.map((x) => x.ormId))
                    .subscribe({
                        next: (response) => {
                            this.afterSaveComplete();
                            this.refreshGrid();
                        },
                        error: (error) => {
                            this.afterError(error);
                        },
                    });
            }
        });
    }

    copyCarsToClipboard(): void {
        this.selectedRowData = this.grid.instance.getSelectedRowsData();
        if (this.selectedRowData != null && this.selectedRowData.length > 0) {
            const carIds = this.selectedRowData.map((eachRow) => {
                return eachRow.carInit.trim() + eachRow.carNo.trim();
            });
            const uniqueCars = [...new Set(carIds)];
            this.clipboard.copy(uniqueCars.join('\n'));
        }
    }

    public onMenuItemClicked(e: unknown): void {
        console.log('onMenuItemClicked', e);
    }

    onGridFastracIdChanged($event: number): void {
        super.onGridFastracIdChanged($event);
        void this.grid.instance.refresh();
    }

    public navigate(currentSelectedRowKeys: number[]): void {
        this.selectedRowKeys = currentSelectedRowKeys;
        const selectedOrmIds = currentSelectedRowKeys.map((ormId) => ormId.toString());

        this.selectionService.setSelectedIds(selectedOrmIds);
        this.updateSelectionToolbar();
        this.errors = [];

        if (currentSelectedRowKeys) {
            if (currentSelectedRowKeys.length === 1) {
                void this.router?.navigate(['detail'], {
                    queryParamsHandling: 'preserve',
                    relativeTo: this.route,
                });

                return;
            }
            if (currentSelectedRowKeys.length > 1) {
                void this.router?.navigate(['multi'], {
                    queryParams: {
                        ids: null,
                    },
                    queryParamsHandling: 'merge',
                    relativeTo: this.route,
                });

                return;
            }
            void this.router?.navigate(['interface-validation'], {
                queryParamsHandling: 'preserve',
            });
        }
    }

    public toggleAllAvailableRows(selectAll: boolean): void {
        console.log(selectAll);

        if (selectAll) {
            this.selectionService.selectAll();
            this.updateSelectionToolbar();
            void this.grid.instance.selectAll();
            return;
        }

        this.selectionService.clearSelectedIds();
        this.updateSelectionToolbar();
        void this.grid.instance.deselectAll();
    }

    private createSelectionToolbarItem(): SelectionToolbarItem {
        return {
            location: 'before',
            widget: 'dxCheckBox',
            options: {
                text: 'Select All',
                value: this.isAvailableRowSelectionIndeterminate
                    ? undefined
                    : this.areAllAvailableRowsSelected,
                enableThreeStateBehavior: true,
                onValueChanged: (event) => {
                    if (event.event) {
                        this.toggleAllAvailableRows(!this.areAllAvailableRowsSelected);
                    }
                },
            },
        };
    }

    private updateSelectionToolbar(): void {
        this.selectionToolbarItems = [this.createSelectionToolbarItem()];
    }

    private getAvailableRowKeys(): number[] {
        return (this.grid?.instance.getVisibleRows() ?? [])
            .filter((row) => row.rowType === 'data' && typeof row.key === 'number')
            .map((row) => row.key);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private afterError(error: ErrorListItem[]) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const customerErrors = [];

        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        this.errors = error;

        this.snackbar.open('Error deleting trip error(s).', 'Error', {
            duration: 3000,
            panelClass: 'error-snack',
        });
    }

    private afterSaveComplete(message?: string) {
        if (!message) {
            this.selectedRowData = [];
            message = 'Trip Error(s) deleted successfully!';
            void this.router.navigate(['interface-validation'], {
                queryParamsHandling: 'preserve',
            });
        }

        this.snackbar.open(message, 'Ok', { duration: 3000 });
    }
}
